// /claims/deck — slide-style presentation of the RELEASED claim cards (P3b).
//
// Reads GET /api/evi-claims/deck (released feed grouped by effect area). One big card SVG per
// slide, inlined (dark mode). The old in-depth/standalone PNG variants and the "prewarm" render
// are retired — the cards are the pre-rendered SVGs of the Evidence board (one renderer).
import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Download, Images, Loader2 } from "lucide-react";
import clsx from "clsx";
import { Link } from "react-router-dom";
import { api, apiUrl } from "../api";
import { InlineSvg } from "./EvidenceReviewTab";
import { CLASS_LABEL, type ReleasedClaim } from "./releasedClaims";

interface DeckCategory {
  area: string;
  claims: ReleasedClaim[];
}
interface Deck {
  categories: DeckCategory[];
  total: number;
}

export default function EviClaimDeckPage() {
  const [deck, setDeck] = useState<Deck | null>(null);
  const [catIdx, setCatIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDeck(await api.get<Deck>("/api/evi-claims/deck"));
    } catch (e) {
      console.error("load deck failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Category change: back to the top of the stack.
  useEffect(() => {
    topRef.current?.scrollIntoView({ block: "start" });
  }, [catIdx]);

  if (loading && !deck) {
    return (
      <div className="mx-auto flex max-w-5xl items-center gap-2 p-6 text-text-3">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading deck …
      </div>
    );
  }

  const cat = deck?.categories[catIdx] ?? null;
  const claims = cat?.claims ?? [];

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6" data-deck-page>
      <div ref={topRef} />

      <div className="sticky top-0 z-20 -mx-4 border-b border-border bg-canvas px-4 pb-3 pt-1 sm:-mx-6 sm:px-6">
        <div className="mb-3 flex items-center gap-3">
          <Link to="/claims" className="inline-flex shrink-0 items-center gap-1 text-sm text-text-3 hover:text-text">
            <ArrowLeft className="h-4 w-4" /> Claims
          </Link>
          <h1 className="truncate font-display text-xl font-semibold text-text">Ectoin® — released claims</h1>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {deck?.categories.map((c, i) => (
            <button
              key={c.area}
              onClick={() => setCatIdx(i)}
              className={clsx(
                "rounded-full border px-3 py-1 text-xs transition-colors",
                i === catIdx
                  ? "border-text bg-text text-canvas"
                  : "border-border bg-surface text-text-2 hover:bg-surface-3",
              )}
            >
              {c.area} <span className="opacity-60">{c.claims.length}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto mt-5 max-w-3xl">
        <div className="mb-4 text-xs text-text-3">{claims.length} cards</div>
        {claims.length === 0 ? (
          <div className="py-16 text-center text-sm text-text-3">
            <Images className="mx-auto mb-3 h-8 w-8 opacity-60" />
            No released cards in this category.
          </div>
        ) : (
          <div className="space-y-8">
            {claims.map((c) => (
              <figure key={c.claim_id} className="space-y-2" data-deck-card={c.claim_id}>
                <div className="rounded-card border border-border bg-surface p-3">
                  <InlineSvg url={c.card_svg} label={c.headline_en} />
                </div>
                <figcaption className="flex flex-wrap items-center justify-between gap-2 px-0.5 text-xs text-text-3">
                  <span>
                    {CLASS_LABEL[c.view_class]}
                    {c.has_caveat && c.caveat ? ` · ${c.caveat}` : ""}
                  </span>
                  <a
                    href={apiUrl(c.card_png)}
                    download={`ectoin-${c.claim_id.replace(/[^a-z0-9]+/gi, "-")}.png`}
                    className="inline-flex items-center gap-1 text-accent-700 hover:text-accent-600 dark:text-accent-300"
                  >
                    <Download className="h-3.5 w-3.5" /> PNG
                  </a>
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
